import React, { useState, useRef } from 'react';
import './SamplerOptions.css';

/**
 * SamplerOptions — collapsible panel on the right edge for the active
 * chopper's playback modes (gate / choke / bleed) and sample transpose.
 * Collapsed it's just "sampler options" with a triangle.
 */
const SamplerOptions = ({ options, transpose, drumLoop }) => {
  const [open, setOpen] = useState(false);
  const drumsFileRef = useRef(null);

  const nudgeTranspose = (delta) => {
    if (!transpose) return;
    transpose.onChange(Math.max(-12, Math.min(12, transpose.value + delta)));
  };

  return (
    <div className="sampler-options">
      <button
        type="button"
        className="sampler-options-toggle"
        onClick={(e) => { setOpen(!open); e.currentTarget.blur(); }}
      >
        sampler options
        <span className={`sampler-options-tri${open ? ' open' : ''}`}>▸</span>
      </button>

      {open && (
        <div className="sampler-options-list">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`sampler-option${o.value ? ' on' : ''}`}
              onClick={(e) => { o.onChange(!o.value); e.currentTarget.blur(); }}
              title={o.hint}
            >
              <span className="sampler-option-box" />
              {o.label}
            </button>
          ))}

          {transpose && (
            <div className="sampler-transpose" title="pitch the whole sample in semitones">
              <span className="sampler-transpose-label">transpose</span>
              <span className="sampler-transpose-controls">
                <button
                  type="button"
                  className="sampler-transpose-btn"
                  onClick={(e) => { nudgeTranspose(-1); e.currentTarget.blur(); }}
                >
                  −
                </button>
                <button
                  type="button"
                  className={`sampler-transpose-value${transpose.value !== 0 ? ' set' : ''}`}
                  onClick={(e) => { transpose.onChange(0); e.currentTarget.blur(); }}
                  title="click to reset"
                >
                  {transpose.value > 0 ? `+${transpose.value}` : transpose.value}st
                </button>
                <button
                  type="button"
                  className="sampler-transpose-btn"
                  onClick={(e) => { nudgeTranspose(1); e.currentTarget.blur(); }}
                >
                  +
                </button>
              </span>
            </div>
          )}

          {drumLoop && (
            <div className="sampler-drumloop" title="loop backing drums under your chops">
              <span className="sampler-drumloop-label">drum loop</span>
              {drumLoop.hasDrums ? (
                <>
                  <button
                    type="button"
                    className={`sampler-drumloop-play${drumLoop.playing ? ' on' : ''}`}
                    onClick={(e) => { drumLoop.onToggle(); e.currentTarget.blur(); }}
                  >
                    {drumLoop.playing ? '■' : '▶'}
                  </button>
                  <button
                    type="button"
                    className="sampler-drumloop-edit"
                    onClick={(e) => { drumLoop.onEdit(); e.currentTarget.blur(); }}
                  >
                    edit
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="sampler-drumloop-edit"
                    onClick={(e) => { drumsFileRef.current?.click(); e.currentTarget.blur(); }}
                    title="upload a drums audio file for this project"
                  >
                    add file
                  </button>
                  <button
                    type="button"
                    className="sampler-drumloop-edit"
                    onClick={(e) => { drumLoop.onUseDefault(); e.currentTarget.blur(); }}
                    title="use the bundled gold digger drums"
                  >
                    gold digger
                  </button>
                </>
              )}
              <input
                ref={drumsFileRef}
                type="file"
                accept="audio/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) drumLoop.onAdd(f);
                  e.target.value = '';
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SamplerOptions;
